import React from 'react';
import { Button } from '@/components/ui/button';
import { Dropdown } from '@/components/ui/dropdown';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  itemLabel?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemLabel = 'items',
}) => {
  const itemsPerPageOptions = [5, 10, 25, 50].map(n => ({ value: n, label: String(n) }));

  return (
    <div className="border-t border-border px-4 py-3 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
      {/* Left: Items per page + showing info */}
      <div className="flex items-center justify-between sm:justify-start sm:gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Per page:</span>
          <Dropdown
            options={itemsPerPageOptions}
            value={itemsPerPage}
            onValueChange={(val) => onItemsPerPageChange(val as number)}
            direction="up"
            triggerClassName="px-2.5 py-1.5 sm:px-3 sm:py-2 min-w-[60px] sm:min-w-[70px] gap-1.5"
          />
        </div>

        <div className="text-xs sm:text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{totalItems === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, totalItems)}</span>
          {' '}of{' '}
          <span className="font-medium text-foreground">{totalItems}</span>
          <span className="hidden sm:inline"> {itemLabel}</span>
        </div>
      </div>

      {/* Right: Page navigation */}
      <div className="flex items-center justify-center sm:justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="hidden sm:flex size-9 p-0"
        >
          <ChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
          className="size-9 p-0"
        >
          <ChevronLeft className="size-4" />
        </Button>

        {/* Page number buttons */}
        <div className="flex items-center gap-1 mx-0.5 sm:mx-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            // On mobile, only show the middle 3 of 5 buttons
            const isEdge = totalPages > 3 && (i === 0 || i === 4);
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className={`size-8 sm:size-9 p-0 text-xs sm:text-sm ${isEdge ? 'hidden sm:flex' : ''}`}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="size-9 p-0"
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="hidden sm:flex size-9 p-0"
        >
          <ChevronsRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export { Pagination };
